/**
 * ES5 兼容的 Object.assign 实现
 * 将所有可枚举属性的值从一个或多个源对象复制到目标对象
 *
 * @template T - 目标对象的类型
 * @param {T} target - 目标对象
 * @param {...object} sources - 源对象列表
 * @returns {T} - 修改后的目标对象
 * @throws {TypeError} - 当目标对象为 null 或 undefined 时抛出
 */
export function objectAssignPolyfill(target) {
    var sources = [];
    for (var _i = 1; _i < arguments.length; _i++) {
        sources[_i - 1] = arguments[_i];
    }
    if (target == null) {
        throw new TypeError('Cannot convert undefined or null to object');
    }
    var to = Object(target);
    for (var _a = 0, sources_1 = sources; _a < sources_1.length; _a++) {
        var source = sources_1[_a];
        if (source != null) {
            var sourceObj = source;
            for (var key in source) {
                if (Object.prototype.hasOwnProperty.call(source, key)) {
                    to[key] = sourceObj[key];
                }
            }
        }
    }
    return to;
}
/**
 * Object.values 的 ES5 实现
 * 返回一个包含对象自身所有可枚举属性值的数组
 *
 * @template T - 对象值的类型
 * @param {Record<string, T>} obj - 要获取值的对象
 * @returns {T[]} - 对象的所有可枚举属性值数组
 */
export function objectValuesPolyfill(obj) {
    var values = [];
    for (var key in obj) {
        if (Object.prototype.hasOwnProperty.call(obj, key)) {
            values.push(obj[key]);
        }
    }
    return values;
}
/**
 * Array.prototype.find 的 ES5 实现
 * 返回数组中满足提供的测试函数的第一个元素的值
 *
 * @template T - 数组元素的类型
 * @param {T[]} arr - 要搜索的数组
 * @param {(value: T, index: number, array: T[]) => boolean} predicate - 用来测试每个元素的函数
 * @param {any} [thisArg] - 执行 predicate 时用作 this 的值
 * @returns {T | undefined} - 满足条件的第一个元素，如果没有则返回 undefined
 * @throws {TypeError} - 当 predicate 不是函数时抛出
 */
export function arrayFindPolyfill(arr, predicate, thisArg) {
    if (typeof predicate !== 'function') {
        throw new TypeError('predicate must be a function');
    }
    var length = arr.length;
    var i = 0;
    while (i < length) {
        var value = arr[i];
        if (predicate.call(thisArg, value, i, arr)) {
            return value;
        }
        i++;
    }
    return undefined;
}
/**
 * Array.prototype.includes 的 ES5 实现
 * 判断一个数组是否包含一个指定的值
 *
 * @template T - 数组元素的类型
 * @param {T[]} arr - 要搜索的数组
 * @param {T} searchElement - 要查找的元素
 * @param {number} [fromIndex=0] - 开始查找的位置。如果为负值，则按升序从 array.length + fromIndex 的索引开始搜索
 * @returns {boolean} - 如果找到指定值返回 true，否则返回 false
 */
export function arrayIncludesPolyfill(arr, searchElement, fromIndex) {
    if (fromIndex === void 0) { fromIndex = 0; }
    if (arr == null) {
        throw new TypeError('arr is null or undefined');
    }
    var len = arr.length;
    if (len === 0) {
        return false;
    }
    var k = fromIndex >= 0 ? fromIndex : Math.max(0, len + fromIndex);
    while (k < len) {
        if (arr[k] === searchElement || (searchElement !== searchElement && arr[k] !== arr[k])) {
            return true;
        }
        k++;
    }
    return false;
}
/**
 * String.prototype.includes 的 ES5 实现
 * 判断一个字符串是否包含另一个字符串
 *
 * @param {string} str - 要搜索的字符串
 * @param {string} searchString - 要查找的字符串
 * @param {number} [position=0] - 开始查找的位置
 * @returns {boolean} - 如果找到指定字符串返回 true，否则返回 false
 */
export function stringIncludesPolyfill(str, searchString, position) {
    if (position === void 0) { position = 0; }
    if (str == null) {
        throw new TypeError('str is null or undefined');
    }
    if (searchString == null) {
        throw new TypeError('searchString is null or undefined');
    }
    return str.indexOf(searchString, position) !== -1;
}
